import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, DollarSign, Calendar, Tag, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import SelectBookingOrProjectModal from '../manage/SelectBookingOrProjectModal';
import { useAppSystem } from '../../hooks/useAppSystem';

interface ExpenseCategory {
  id: string;
  name: string;
  color?: string;
}

interface AddManualExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  businessId: string;
  categories: ExpenseCategory[];
  onCategoriesChange: () => void;
  initialBookingId?: string;
  initialProjectId?: string;
}

export default function AddManualExpenseModal({
  isOpen,
  onClose,
  onSave,
  businessId,
  categories,
  onCategoriesChange,
  initialBookingId,
  initialProjectId
}: AddManualExpenseModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSelectBookingOrProjectModalOpen, setIsSelectBookingOrProjectModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<{ id: string; title: string; booking_number: number | null } | null>(null);
  const [selectedProject, setSelectedProject] = useState<{ id: string; title: string } | null>(null);
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  const hasCalendarApp = coreApps.some(app => app.app?.slug === 'calendar') || 
                        installedAddons.some(app => app.app?.slug === 'calendar');
  const hasProjectsApp = coreApps.some(app => app.app?.slug === 'projects') || 
                         installedAddons.some(app => app.app?.slug === 'projects');
  
  // Form fields
  const [bookingId, setBookingId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryId, setCategoryId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [taxBreakdown, setTaxBreakdown] = useState<{ name: string; rate: string; amount: string }[]>([]);
  const [notes, setNotes] = useState('');
  const [isTaxDeductible, setIsTaxDeductible] = useState(true);

  // Inline add-category
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Category already exists');
      return;
    }
    setIsSavingCategory(true);
    try {
      const maxOrder = categories.reduce((max, c: any) => Math.max(max, c.order_index || 0), -1);
      const { data, error } = await supabase
        .from('expense_categories')
        .insert({ business_id: businessId, name: trimmed, color: '#3B82F6', order_index: maxOrder + 1, is_system: false, is_active: true })
        .select()
        .single();
      if (error) throw error;
      toast.success(`Category "${trimmed}" added`);
      setCategoryId(data.id);
      setNewCategoryName('');
      setIsAddingCategory(false);
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error adding category:', error);
      toast.error(error.message || 'Failed to add category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      // Pre-select booking or project if provided
      if (initialBookingId) {
        setBookingId(initialBookingId);
        setProjectId('');
        setSelectedProject(null);
        // Fetch booking details
        fetchBookingDetails(initialBookingId);
      } else if (initialProjectId) {
        setProjectId(initialProjectId);
        setBookingId('');
        setSelectedBooking(null);
        // Fetch project details
        fetchProjectDetails(initialProjectId);
      } else {
        setBookingId('');
        setProjectId('');
        setSelectedBooking(null);
        setSelectedProject(null);
      }
      
      setAmount('');
      setDescription('');
      setMerchantName('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setCategoryId('');
      setPaymentMethod('');
      setTaxBreakdown([]);
      setNotes('');
    }
  }, [isOpen, initialBookingId, initialProjectId]);

  const fetchBookingDetails = async (bookingId: string) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, title, booking_number')
        .eq('id', bookingId)
        .single();

      if (!error && data) {
        setSelectedBooking({
          id: data.id,
          title: data.title || 'Untitled Booking',
          booking_number: data.booking_number
        });
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };

  const fetchProjectDetails = async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, title')
        .eq('id', projectId)
        .single();

      if (!error && data) {
        setSelectedProject({
          id: data.id,
          title: data.title || 'Untitled Project'
        });
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const handleSubmit = async () => {
    if (!amount || !description || !expenseDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Sync booking/project relationship
      let finalBookingId = bookingId || null;
      let finalProjectId = projectId || null;

      // If expense is assigned to a booking, check if booking has a linked project
      if (finalBookingId && !finalProjectId) {
        try {
          const { data: bookingData, error: bookingError } = await supabase
            .from('bookings')
            .select('project_id')
            .eq('id', finalBookingId)
            .single();
          
          if (bookingError) {
            console.error('Error fetching booking project_id:', bookingError);
          } else if (bookingData?.project_id) {
            console.log('Found linked project from booking:', bookingData.project_id);
            finalProjectId = bookingData.project_id;
          }
        } catch (error) {
          console.error('Error syncing booking to project:', error);
        }
      }

      // If expense is assigned to a project, check if project has a linked booking
      if (finalProjectId && !finalBookingId) {
        try {
          const { data: projectData, error: projectError } = await supabase
            .from('projects')
            .select('booking_id')
            .eq('id', finalProjectId)
            .single();
          
          if (projectError) {
            console.error('Error fetching project booking_id:', projectError);
          } else if (projectData?.booking_id) {
            console.log('Found linked booking from project:', projectData.booking_id);
            finalBookingId = projectData.booking_id;
          }
        } catch (error) {
          console.error('Error syncing project to booking:', error);
        }
      }

      // Also check reverse: if we have a booking, check if any project has this booking_id
      if (finalBookingId && !finalProjectId) {
        try {
          const { data: projectWithBooking, error: reverseError } = await supabase
            .from('projects')
            .select('id')
            .eq('booking_id', finalBookingId)
            .maybeSingle();
          
          if (reverseError) {
            console.error('Error fetching project by booking_id:', reverseError);
          } else if (projectWithBooking?.id) {
            console.log('Found linked project via booking_id:', projectWithBooking.id);
            finalProjectId = projectWithBooking.id;
          }
        } catch (error) {
          console.error('Error checking reverse booking-project link:', error);
        }
      }

      console.log('Final expense assignment:', { finalBookingId, finalProjectId });

      // Build tax data
      const parsedTaxBreakdown = taxBreakdown
        .filter(t => t.name.trim() && parseFloat(t.amount) > 0)
        .map(t => ({
          name: t.name.trim(),
          rate: t.rate ? parseFloat(t.rate) : null,
          amount: parseFloat(t.amount) || 0,
        }));
      const totalTax = parsedTaxBreakdown.reduce((sum, t) => sum + t.amount, 0);

      // Create expense
      const expenseData: any = {
        business_id: businessId,
        booking_id: finalBookingId,
        project_id: finalProjectId,
        category_id: categoryId || null,
        amount: parseFloat(amount),
        currency: 'USD',
        description,
        merchant_name: merchantName || null,
        expense_date: expenseDate,
        payment_method: paymentMethod || null,
        tax_amount: totalTax,
        tax_breakdown: parsedTaxBreakdown,
        is_tax_deductible: isTaxDeductible,
        receipt_image_path: null,
        receipt_image_url: null,
        notes: notes || null,
        created_by: user?.id || null,
        ai_categorized: false,
        ai_category_suggestion: null,
        ai_confidence_score: null,
        ai_extracted_data: null,
        approval_status: 'approved',
      };

      const { error: expenseError } = await supabase
        .from('expenses')
        .insert(expenseData);

      if (expenseError) throw expenseError;

      toast.success('Expense added successfully');
      
      // Reset form
      setAmount('');
      setDescription('');
      setMerchantName('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setCategoryId('');
      setPaymentMethod('');
      setTaxBreakdown([]);
      setNotes('');

      onSave();
    } catch (error: any) {
      console.error('Error creating expense:', error);
      toast.error(error.message || 'Failed to create expense');
    } finally {
      setIsProcessing(false);
    }
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden relative">
        {/* Header */}
        <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
          <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Add Manual Expense</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className={`${isMobile ? 'h-5 w-5' : 'h-5 w-5'}`} />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden min-w-0 ${isMobile ? 'px-4 py-4' : 'px-6 py-6'} space-y-${isMobile ? '4' : '4'}`}>
          {/* Form Fields */}
          <div className="space-y-4">
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`w-full pl-10 pr-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="0.00"
                    style={isMobile ? { fontSize: '16px' } : undefined}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="date"
                    value={expenseDate}
                    onChange={(e) => setExpenseDate(e.target.value)}
                    className={`w-full pl-10 pr-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    style={isMobile ? { fontSize: '16px' } : undefined}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="What was this expense for?"
                style={isMobile ? { fontSize: '16px' } : undefined}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Merchant/Vendor
              </label>
              <input
                type="text"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Store or vendor name"
                style={isMobile ? { fontSize: '16px' } : undefined}
              />
            </div>

            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                {isAddingCategory ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      className={`flex-1 pl-3 pr-3 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="Category name"
                      autoFocus
                      style={isMobile ? { fontSize: '16px' } : undefined}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAddCategory();
                        if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(''); }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      disabled={isSavingCategory || !newCategoryName.trim()}
                      className="px-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                    >
                      {isSavingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }}
                      className="px-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      value={categoryId}
                      onChange={(e) => {
                        if (e.target.value === '__add_new__') {
                          setIsAddingCategory(true);
                        } else {
                          setCategoryId(e.target.value);
                        }
                      }}
                      className={`w-full pl-10 pr-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      style={isMobile ? { fontSize: '16px' } : undefined}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                      <option value="__add_new__">+ Add New Category</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  style={isMobile ? { fontSize: '16px' } : undefined}
                >
                  <option value="">Select method</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="check">Check</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Tax Breakdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tax
              </label>
              <div className="space-y-2">
                {taxBreakdown.map((tax, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={tax.name}
                      onChange={e => {
                        const updated = [...taxBreakdown];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setTaxBreakdown(updated);
                      }}
                      className={`w-24 px-3 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="GST"
                      style={isMobile ? { fontSize: '16px' } : undefined}
                    />
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        value={tax.amount}
                        onChange={e => {
                          const updated = [...taxBreakdown];
                          updated[idx] = { ...updated[idx], amount: e.target.value };
                          setTaxBreakdown(updated);
                        }}
                        className={`w-full pl-8 pr-3 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        placeholder="0.00"
                        style={isMobile ? { fontSize: '16px' } : undefined}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setTaxBreakdown(taxBreakdown.filter((_, i) => i !== idx))}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setTaxBreakdown([...taxBreakdown, { name: '', rate: '', amount: '' }])}
                  className="flex items-center gap-1.5 text-sm px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Tax
                </button>
                {taxBreakdown.length > 0 && (
                  <div className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-xl text-sm">
                    <span className="text-gray-500 font-medium">Total Tax</span>
                    <span className="font-semibold text-gray-900">
                      ${taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {amount && (
                  <div className="flex justify-between items-center px-3 py-2 bg-blue-50 rounded-xl text-sm">
                    <span className="text-blue-700 font-medium">Grand Total</span>
                    <span className="font-bold text-blue-900">
                      ${((parseFloat(amount) || 0) + taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={`w-full px-4 ${isMobile ? 'py-3 text-base' : 'py-2'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y`}
                placeholder="Additional notes..."
                style={isMobile ? { fontSize: '16px' } : undefined}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 bg-white border-t border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} ${isMobile ? 'flex-col gap-2' : 'flex items-center justify-end gap-3'}`}>
          {isMobile && (
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !amount || !description || !expenseDate}
              className={`w-full px-4 py-3.5 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium touch-manipulation`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Expense'
              )}
            </button>
          )}
          <button
            onClick={handleClose}
            className={`${isMobile ? 'w-full px-4 py-3 text-base' : 'px-4 py-2'} text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Cancel
          </button>
          {!isMobile && (
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !amount || !description || !expenseDate}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Expense'
              )}
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {modalContent}
      {/* Select Booking or Project Modal */}
      <SelectBookingOrProjectModal
        isOpen={isSelectBookingOrProjectModalOpen}
        onClose={() => setIsSelectBookingOrProjectModalOpen(false)}
        onSelectBooking={(booking) => {
          setSelectedBooking({
            id: booking.id,
            title: booking.title || 'Untitled Booking',
            booking_number: booking.booking_number
          });
          setBookingId(booking.id);
          setSelectedProject(null);
          setProjectId('');
          setIsSelectBookingOrProjectModalOpen(false);
        }}
        onSelectProject={(project) => {
          setSelectedProject({
            id: project.id,
            title: project.title
          });
          setProjectId(project.id);
          setSelectedBooking(null);
          setBookingId('');
          setIsSelectBookingOrProjectModalOpen(false);
        }}
        businessId={businessId}
        hasCalendarApp={hasCalendarApp}
        hasProjectsApp={hasProjectsApp}
      />
    </>,
    document.body
  );
}

